import type { Capability, Manifest, View } from '@clawharbor/manifest';
import type { CapabilityResult, Row } from '@clawharbor/render-core';

/**
 * The whole point of the web surface.
 *
 * An agent calling a WebMCP tool must produce a VISIBLE change on the real page.
 * That only happens if tool execution and human interaction write to the same
 * place, so this store is the single mutable thing in the app and both paths go
 * through `applyResult`. If the agent and the human could disagree about what is
 * on screen, the product has already failed — WebMCP's stated goal is that the
 * page stays the interface, not that an agent gets a private copy of it.
 */

export type ResultOrigin = 'agent' | 'human';

export interface ResultRecord {
  capabilityId: string;
  view: string;
  result: CapabilityResult;
  /** Where the human finishes the job — always the operator's own site. */
  deepLink?: string;
  /** Narration from the server when it supplied one, else render-core's summary. */
  summary: string;
  input: Record<string, unknown>;
  receivedAt: number;
  durationMs: number;
  origin: ResultOrigin;
}

export interface ToolLogEntry {
  name: string;
  origin: ResultOrigin;
  ok: boolean;
  durationMs: number;
  at: number;
  count?: number;
  error?: string;
}

/**
 * A staged, UNCOMMITTED selection.
 *
 * ClawHarbor never buys anything. A `stage` capability puts a proposal in front
 * of the human behind a real control only a human can press; the transaction
 * happens on the operator's own checkout. This record is that proposal, and its
 * existence is why `stage` is a distinct capability kind rather than a write.
 */
export interface StagedSelection {
  label: string;
  deepLink?: string;
  stagedAt: string;
  fields: Row;
}

export interface WebMcpStatus {
  /** False means: no agent surface here. The page must still be a working website. */
  available: boolean;
  source: 'document' | 'navigator' | 'none';
  registered: number;
  /** Names the user agent actually accepted, for the debug strip. */
  toolNames: string[];
  error?: string;
}

export interface AppState {
  tenant: string;
  status: 'booting' | 'ready' | 'error';
  error?: string;
  manifest?: Manifest;
  /** Which capability's panel the human is looking at. */
  activeCapability?: string;
  /** Keyed by view id, so two capabilities sharing a view share a result slot. */
  results: Record<string, ResultRecord>;
  staged?: StagedSelection;
  toolLog: ToolLogEntry[];
  webmcp: WebMcpStatus;
  /** capability id -> in flight. Drives the same spinner for agent and human. */
  pending: Record<string, boolean>;
  /** Bumped on every mutation, so the debug strip can prove a repaint happened. */
  revision: number;
}

type Listener = (state: AppState) => void;

const MAX_LOG = 25;

export class Store {
  #state: AppState;
  #listeners = new Set<Listener>();

  constructor(tenant: string) {
    this.#state = {
      tenant,
      status: 'booting',
      results: {},
      toolLog: [],
      webmcp: { available: false, source: 'none', registered: 0, toolNames: [] },
      pending: {},
      revision: 0,
    };
  }

  get state(): AppState {
    return this.#state;
  }

  subscribe(fn: Listener): () => void {
    this.#listeners.add(fn);
    return () => {
      this.#listeners.delete(fn);
    };
  }

  #commit(patch: Partial<AppState>): void {
    this.#state = { ...this.#state, ...patch, revision: this.#state.revision + 1 };
    for (const fn of this.#listeners) fn(this.#state);
  }

  setManifest(manifest: Manifest): void {
    const first =
      manifest.capabilities.find((c) => c.surfaces.web.register) ?? manifest.capabilities[0];
    this.#commit({
      manifest,
      status: 'ready',
      error: undefined,
      activeCapability: this.#state.activeCapability ?? first?.id,
    });
  }

  fail(message: string): void {
    this.#commit({ status: 'error', error: message });
  }

  setActiveCapability(id: string): void {
    this.#commit({ activeCapability: id });
  }

  setPending(capabilityId: string, pending: boolean): void {
    const next = { ...this.#state.pending };
    if (pending) next[capabilityId] = true;
    else delete next[capabilityId];
    this.#commit({ pending: next });
  }

  setWebMcp(patch: Partial<WebMcpStatus>): void {
    this.#commit({ webmcp: { ...this.#state.webmcp, ...patch } });
  }

  /**
   * THE repaint. Called identically from the WebMCP `execute()` path and from the
   * page's own form — which is exactly why an agent call is visible.
   */
  applyResult(record: ResultRecord): void {
    this.#commit({
      results: { ...this.#state.results, [record.view]: record },
      activeCapability: record.capabilityId,
    });
  }

  stage(selection: StagedSelection): void {
    this.#commit({ staged: selection });
  }

  clearStaged(): void {
    this.#commit({ staged: undefined });
  }

  log(entry: ToolLogEntry): void {
    this.#commit({ toolLog: [entry, ...this.#state.toolLog].slice(0, MAX_LOG) });
  }

  capability(id: string): Capability | undefined {
    return this.#state.manifest?.capabilities.find((c) => c.id === id || c.name === id);
  }

  view(id: string): View | undefined {
    return this.#state.manifest?.views.find((v) => v.id === id);
  }
}
